import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatError } from "../../lib/api";
import { SALayout } from "../../components/superadmin/SALayout";
import { formatCurrency, formatDate, formatDateTime } from "../../lib/format";
import { useSAAuth } from "../../contexts/SAAuthContext";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer,
} from "recharts";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { ArrowLeft, Save, ArrowRightLeft, Pencil, Copy, Check, X } from "lucide-react";

const TABS = ["Overview", "Customers", "Bottle Tabs", "Campaigns", "WhatsApp Log", "Activity / Notes", "Usage", "Features", "WA Templates", "WA Config", "POS Integration", "Payments", "Transfer"];

// Rendered from a list so adding a provider later is a one-line change.
const POS_PROVIDERS = [{ value: "petpooja", label: "Petpooja" }];

const NON_RETRYABLE_TEMPLATES = new Set([
  "tabon_tab_verification",
  "tabon_tab_otp",
  "tabon_tab_otp_v2",
  "tabon_redeem_otp",
  "tabon_redemption_message",
]);

const FEATURE_DEFS = [
  { key: "loyalty_points", label: "Loyalty Points",       desc: "Points earning, redemption, milestone rewards and billing OTP. Customers earn points on every visit and redeem against future bills." },
  { key: "bottle_locker", label: "Bottle Locker",         desc: "Digital storage for unfinished bottles. Customers open tabs, track remaining ml, and deduct via OTP." },
  { key: "stamp_cards",   label: "Stamp Cards",           desc: "Digital punch cards. Customers earn stamps on visits and unlock a configured reward at completion." },
  { key: "qr_menu",       label: "QR Menu",               desc: "Public digital menu accessible via QR code. Required for Customer QR Ordering." },
  { key: "campaigns",     label: "Campaigns",             desc: "WhatsApp marketing — event announcements, win-back messages, and birthday offers sent to segmented customer lists." },
  { key: "feedback",      label: "Feedback",              desc: "Post-visit feedback request sent via WhatsApp 60 minutes after qualifying visits." },
  { key: "call_waiter",   label: "Call Waiter",           desc: "Table-side waiter request button on the public menu." },
  { key: "ordering",      label: "QR Ordering & POS",     desc: "Live orders board, KOT printing, payment recording, WhatsApp notifications, daily sales summary. Works with or without the public QR menu." },
  { key: "ordering_qr",   label: "Customer QR Ordering",  desc: "Adds cart and checkout to the public QR menu so customers self-order. Requires QR Menu published AND QR Ordering & POS enabled." },
];
const PLAN_OPTS = ["Trial", "Pro", "Growth", "Enterprise"];
const STATUS_OPTS = ["Trial", "Active", "Paused", "Churned"];
const PAYMENT_OPTS = ["Trial", "Paid", "Overdue", "Failed"];
const CHECKLIST = [
  "Onboarding call done",
  "Staff trained",
  "Menu / bottle list uploaded",
  "First bottle tab created",
  "Meta WhatsApp Cloud API integration live",
  "First WhatsApp campaign sent",
];

function OwnerContactEditor({ venue, onSaved }) {
  const [editing, setEditing] = React.useState(false);
  const [ownerName, setOwnerName] = React.useState(venue?.owner_name || "");
  const [ownerPhone, setOwnerPhone] = React.useState(venue?.owner_phone || "");
  const [ownerEmail, setOwnerEmail] = React.useState(venue?.owner_email || "");
  const [saving, setSaving] = React.useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/venues/${venue.id}/meta`, {
        owner_name: ownerName.trim() || null,
        owner_phone: ownerPhone.trim() || null,
        owner_email: ownerEmail.trim() || null,
      });
      toast.success("Owner contact updated");
      setEditing(false);
      onSaved();
    } catch {
      toast.error("Failed to update owner contact");
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        className="text-xs text-[#9D00FF] hover:underline mt-1 block"
      >
        Edit owner contact
      </button>
    );
  }

  return (
    <div className="bg-[#0F0F0F] border border-white/10 rounded-xl p-4 mt-3 flex flex-col gap-3">
      <p className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0]">
        Edit Owner Contact
      </p>
      {[
        { label: "Owner name", value: ownerName, set: setOwnerName, placeholder: "e.g. Narayan Sharma" },
        { label: "WhatsApp number", value: ownerPhone, set: setOwnerPhone, placeholder: "e.g. 9820074695" },
        { label: "Email", value: ownerEmail, set: setOwnerEmail, placeholder: "e.g. owner@venue.com" },
      ].map(({ label, value, set, placeholder }) => (
        <div key={label}>
          <label className="text-xs text-[#A0A0A0] block mb-1">{label}</label>
          <input
            type="text"
            value={value}
            onChange={e => set(e.target.value)}
            placeholder={placeholder}
            className="w-full bg-[#1A1A1A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[#9D00FF] focus:outline-none"
          />
        </div>
      ))}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => setEditing(false)}
          className="px-4 py-2 rounded-lg border border-white/10 text-[#A0A0A0] text-xs hover:border-white/30 transition"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="px-4 py-2 rounded-lg bg-[#9D00FF] text-white text-xs font-bold disabled:opacity-50 transition"
        >
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}

export default function SAVenueDetail() {
  const { vid } = useParams();
  const { user } = useSAAuth();
  const isAdmin = user?.role === "Admin";
  const [venue, setVenue] = useState(null);
  const [tab, setTab] = useState("Overview");
  const [customers, setCustomers] = useState([]);
  const [tabs, setTabs] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [waLog, setWaLog] = useState([]);
  const [notes, setNotes] = useState([]);
  const [usage, setUsage] = useState({});
  const [newNote, setNewNote] = useState("");
  const [waTemplates, setWaTemplates] = useState([]);
  // Local state for the points expiry months number input — avoids API call per keystroke
  const [expiryMonths, setExpiryMonths] = useState(12);
  // Inline venue-code editing
  const [editingCode, setEditingCode] = useState(false);
  const [codeInput, setCodeInput] = useState("");
  const [codeSaving, setCodeSaving] = useState(false);
  // Setup admin
  const [setupAdmin, setSetupAdmin] = useState(null); // null = loading, false = none, {email} = exists
  const [setupAdminLoading, setSetupAdminLoading] = useState(false);
  // WA log date range filter
  const [waFrom, setWaFrom] = useState("");
  const [waTo, setWaTo] = useState("");
  const [waLogTotal, setWaLogTotal] = useState(0);
  const [waLogSkip, setWaLogSkip] = useState(0);
  const WA_PAGE = 100;
  // WA Config tab
  const [waConfig, setWaConfig] = useState(null);
  const [waConfigForm, setWaConfigForm] = useState({ meta_phone_number_id: "", meta_token: "" });
  const [waConfigSaving, setWaConfigSaving] = useState(false);
  const [retrying, setRetrying] = useState({});
  // POS Integration tab
  const [posIntegrations, setPosIntegrations] = useState(null); // null = not loaded yet
  const [posLog, setPosLog] = useState([]);
  const [posForm, setPosForm] = useState({ provider: "petpooja", external_branch_code: "", notes: "" });
  const [posBusy, setPosBusy] = useState(false);
  const [posNewToken, setPosNewToken] = useState(null); // { token, message } — shown once
  const [posLogOpen, setPosLogOpen] = useState(false);

  const reload = useCallback(async () => {
    const { data } = await api.get(`/admin/venues/${vid}`);
    setVenue(data);
  }, [vid]);

  const loadSetupAdmin = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/venues/${vid}/setup-admin`);
      setSetupAdmin(data.exists ? data : false);
    } catch { setSetupAdmin(false); }
  }, [vid]);

  const loadPos = useCallback(async () => {
    try {
      const [ints, log] = await Promise.all([
        api.get(`/admin/pos-integrations`, { params: { venue_id: vid } }),
        api.get(`/admin/pos-request-log`, { params: { venue_id: vid, limit: 50 } }).catch(() => ({ data: [] })),
      ]);
      setPosIntegrations(ints.data || []);
      setPosLog(log.data || []);
    } catch (err) { toast.error(formatError(err)); setPosIntegrations([]); }
  }, [vid]);

  const posCreate = async () => {
    setPosBusy(true);
    try {
      const { data } = await api.post(`/admin/pos-integrations`, {
        venue_id: vid,
        provider: posForm.provider,
        external_branch_code: posForm.external_branch_code.trim() || null,
        notes: posForm.notes.trim() || null,
      });
      setPosNewToken({ token: data.token, message: data.message });
      setPosForm({ provider: "petpooja", external_branch_code: "", notes: "" });
      await loadPos();
      await reload();
    } catch (err) { toast.error(formatError(err)); }
    finally { setPosBusy(false); }
  };

  const posRotate = async (id) => {
    if (!window.confirm("Rotate the token? The provider must be updated with the new token or their calls will start failing.")) return;
    setPosBusy(true);
    try {
      const { data } = await api.post(`/admin/pos-integrations/${id}/rotate`);
      setPosNewToken({ token: data.token, message: data.message });
      await loadPos();
    } catch (err) { toast.error(formatError(err)); }
    finally { setPosBusy(false); }
  };

  const posSetStatus = async (id, status) => {
    if (!window.confirm(status === "disabled"
      ? "Disable this integration? Inbound POS calls will be rejected."
      : "Enable this integration?")) return;
    setPosBusy(true);
    try {
      await api.patch(`/admin/pos-integrations/${id}`, { status });
      await loadPos();
      await reload();
    } catch (err) { toast.error(formatError(err)); }
    finally { setPosBusy(false); }
  };

  const posDelete = async (id) => {
    if (!window.confirm("Remove this POS link entirely? This cannot be undone.")) return;
    setPosBusy(true);
    try {
      await api.delete(`/admin/pos-integrations/${id}`);
      await loadPos();
      await reload();
    } catch (err) { toast.error(formatError(err)); }
    finally { setPosBusy(false); }
  };

  useEffect(() => { reload(); loadSetupAdmin(); }, [reload, loadSetupAdmin]);

  // Sync local months input when venue data arrives or changes
  useEffect(() => {
    if (venue?.points_expiry_months) setExpiryMonths(venue.points_expiry_months);
  }, [venue?.points_expiry_months]);

  // Re-fetch venue data when the SA user switches back to this tab so that
  // feature flag changes made in another tab are visible without a page reload.
  useEffect(() => {
    const handleFocus = () => reload();
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [reload]);

  useEffect(() => {
    if (tab === "Customers" && customers.length === 0) api.get(`/admin/venues/${vid}/customers`).then((r) => setCustomers(r.data));
    if (tab === "Bottle Tabs" && tabs.length === 0) api.get(`/admin/venues/${vid}/tabs`).then((r) => setTabs(r.data));
    if (tab === "Campaigns" && campaigns.length === 0) {
      api.get(`/admin/venues/${vid}/campaigns`).then((r) => {
        setCampaigns(r.data.campaigns || []);
        setPromotions(r.data.promotions || []);
      });
    }
    if (tab === "Activity / Notes" && notes.length === 0) api.get(`/admin/venues/${vid}/notes`).then((r) => setNotes(r.data));
    if (tab === "Usage" && !usage.weeks) api.get(`/admin/venues/${vid}/usage`).then((r) => setUsage(r.data));
    if (tab === "WA Templates" && waTemplates.length === 0) api.get(`/admin/venues/${vid}/templates`).then((r) => setWaTemplates(r.data)).catch(() => {});
    if (tab === "WA Config" && !waConfig) {
      api.get(`/admin/venues/${vid}/whatsapp-config`)
        .then((r) => {
          setWaConfig(r.data);
          setWaConfigForm({
            meta_phone_number_id: r.data.meta_phone_number_id || "",
            meta_token: "",
          });
        })
        .catch(() => {});
    }
    if (tab === "POS Integration" && posIntegrations === null) loadPos();
    // eslint-disable-next-line
  }, [tab]);

  // Reset skip when filters change
  useEffect(() => {
    setWaLogSkip(0);
    setWaLog([]);
  }, [waFrom, waTo, tab]);

  useEffect(() => {
    if (tab !== "WhatsApp Log") return;
    const params = { skip: waLogSkip, limit: WA_PAGE };
    if (waFrom) params.from_date = waFrom;
    if (waTo) params.to_date = waTo;
    api.get(`/admin/venues/${vid}/whatsapp-log`, { params }).then((r) => {
      const data = r.data;
      setWaLogTotal(data.total || 0);
      if (waLogSkip === 0) {
        setWaLog(data.rows || []);
      } else {
        setWaLog(prev => [...prev, ...(data.rows || [])]);
      }
    });
  }, [tab, vid, waFrom, waTo, waLogSkip]);

  const handleRetry = async (logId) => {
    setRetrying(prev => ({ ...prev, [logId]: true }));
    try {
      await api.post(`/admin/venues/${vid}/whatsapp-log/${logId}/retry`);
      setWaLogSkip(0);
      const { data } = await api.get(`/admin/venues/${vid}/whatsapp-log`, {
        params: { skip: 0, limit: WA_PAGE },
      });
      setWaLog(data.rows || []);
      setWaLogTotal(data.total || 0);
      toast.success("Retry sent");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Retry failed");
    } finally {
      setRetrying(prev => ({ ...prev, [logId]: false }));
    }
  };

  const addNote = async () => {
    if (!newNote.trim()) return;
    try {
      await api.post(`/admin/venues/${vid}/notes`, { text: newNote.trim() });
      setNewNote("");
      const { data } = await api.get(`/admin/venues/${vid}/notes`);
      setNotes(data);
      toast.success("Note added");
    } catch (e) { toast.error(formatError(e)); }
  };

  const saveMeta = async (patch, successMsg = "Saved") => {
    try {
      const { data } = await api.put(`/admin/venues/${vid}/meta`, patch);
      setVenue(data);
      toast.success(successMsg);
      if (notes.length) api.get(`/admin/venues/${vid}/notes`).then((r) => setNotes(r.data));
    } catch (e) { toast.error(formatError(e)); }
  };

  const toggleFeature = async (featureKey, newValue) => {
    try {
      await api.patch(`/admin/venues/${vid}/features`, { [featureKey]: newValue });
      setVenue(prev => ({
        ...prev,
        features: { ...(prev.features || {}), [featureKey]: newValue },
      }));
      toast.success(`${featureKey.replace(/_/g, " ")} ${newValue ? "enabled" : "disabled"} ✓`);
    } catch (e) { toast.error(formatError(e)); }
  };

  const updateVenueSetting = async (key, value) => {
    try {
      await api.patch(`/admin/venues/${vid}/settings`, { [key]: value });
      setVenue(prev => ({ ...prev, [key]: value }));
      toast.success("Setting saved ✓");
    } catch (e) { toast.error(formatError(e)); }
  };

  const saveVenueCode = async () => {
    const code = codeInput.trim().toUpperCase();
    if (!code) { toast.error("Enter a venue code"); return; }
    setCodeSaving(true);
    try {
      await api.patch(`/admin/venues/${vid}/settings`, { venue_code: code });
      setVenue(prev => ({ ...prev, venue_code: code }));
      setEditingCode(false);
      toast.success(`Venue code set to ${code}`);
      // Reload setup admin status since email depends on code
      await loadSetupAdmin();
    } catch (e) { toast.error(formatError(e)); }
    finally { setCodeSaving(false); }
  };

  const createSetupAdmin = async () => {
    setSetupAdminLoading(true);
    try {
      const { data } = await api.post(`/admin/venues/${vid}/setup-admin`);
      if (data.already_exists) toast.info(`Setup admin already exists: ${data.email}`);
      else toast.success(`Setup admin created: ${data.email}`);
      setSetupAdmin({ exists: true, email: data.email });
    } catch (e) { toast.error(formatError(e)); }
    finally { setSetupAdminLoading(false); }
  };

  const removeSetupAdmin = async () => {
    if (!window.confirm("Remove the setup admin account? They will immediately lose access.")) return;
    setSetupAdminLoading(true);
    try {
      await api.delete(`/admin/venues/${vid}/setup-admin`);
      toast.success("Setup admin removed");
      setSetupAdmin(false);
    } catch (e) { toast.error(formatError(e)); }
    finally { setSetupAdminLoading(false); }
  };

  if (!venue) return <SALayout><div className="text-[#A0A0A0]">Loading…</div></SALayout>;

  return (
    <SALayout>
      <Link to="/admin/venues" className="inline-flex items-center gap-2 text-sm text-[#A0A0A0] hover:text-white mb-4" data-testid="sa-venue-back">
        <ArrowLeft size={14} /> Back to directory
      </Link>

      <div className="bg-[#1A1A1A] border border-white/10 rounded-xl p-6 mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black tracking-tight">{venue.name}</h1>
          <div className="text-sm text-[#A0A0A0] mt-1 flex items-center gap-2 flex-wrap">
            {venue.city || "—"} · {venue.type || "—"} · Code{" "}
            {editingCode ? (
              <span className="inline-flex items-center gap-1">
                <input
                  autoFocus
                  value={codeInput}
                  onChange={(e) => setCodeInput(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))}
                  onKeyDown={(e) => { if (e.key === "Enter") saveVenueCode(); if (e.key === "Escape") setEditingCode(false); }}
                  maxLength={12}
                  placeholder="ABC123"
                  className="w-24 bg-[#0A0A0A] border border-[#9D00FF]/60 rounded px-2 py-0.5 text-sm font-mono text-white focus:outline-none focus:border-[#9D00FF]"
                />
                <button onClick={saveVenueCode} disabled={codeSaving} title="Save" className="text-emerald-400 hover:text-emerald-300 disabled:opacity-50">
                  <Check size={14} />
                </button>
                <button onClick={() => setEditingCode(false)} title="Cancel" className="text-[#A0A0A0] hover:text-white">
                  <X size={14} />
                </button>
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5">
                <span className="text-white font-mono">{venue.venue_code || "—"}</span>
                {isAdmin && (
                  <button
                    onClick={() => { setCodeInput(venue.venue_code || ""); setEditingCode(true); }}
                    title="Edit venue code"
                    className="text-[#A0A0A0] hover:text-white transition-colors"
                  >
                    <Pencil size={12} />
                  </button>
                )}
              </span>
            )}
          </div>
          <div className="text-xs text-[#A0A0A0] mt-1">
            Owner: {venue.owner_name || "—"} · {venue.owner_phone || "—"} · {venue.owner_email || "—"}
          </div>
          <OwnerContactEditor venue={venue} onSaved={reload} />
        </div>
        <div className="flex items-center gap-3">
          <Kpi label="Plan" value={venue.plan} />
          <Kpi label="Status" value={venue.status} />
          <Kpi label="MRR" value={formatCurrency(venue.monthly_amount)} />
          <Kpi label="Health" value={`${venue.health_label} (${venue.health_score ?? 0}%)`} />
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            data-testid={`sa-vd-tab-${t.replace(/\W/g, "-").toLowerCase()}`}
            className={`px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-wider border ${
              tab === t ? "bg-[#9D00FF] border-[#9D00FF] text-white" : "bg-transparent border-white/10 text-white/70 hover:border-white/30"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === "Overview" && (
        <div className="grid lg:grid-cols-2 gap-4">
          <Card title="Account & Billing">
            <MetaEditor venue={venue} canEdit={isAdmin} onSave={saveMeta} />
          </Card>
          <Card title="Onboarding Checklist">
            <ChecklistEditor venue={venue} canEdit={isAdmin} onSave={(checklist) => saveMeta({ checklist }, "Checklist updated")} />
          </Card>
          <Card title="Setup & Onboarding">
            <p className="text-xs text-[#A0A0A0] mb-4">
              Create a temporary admin account (<span className="font-mono text-white/70">setup+{'{code}'}@tabon.app</span>) so the venue can
              log in during onboarding. Remove it once training is complete.
            </p>
            {!venue.venue_code ? (
              <div className="text-xs text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2">
                Set a venue code (click the pencil icon next to "Code" in the header) before creating a setup admin.
              </div>
            ) : setupAdmin === null ? (
              <div className="text-xs text-[#A0A0A0]">Checking…</div>
            ) : setupAdmin && setupAdmin.exists ? (
              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-3 py-2">
                  <Check size={13} className="text-emerald-400 shrink-0" />
                  <span className="text-sm text-white font-mono">{setupAdmin.email}</span>
                  <button
                    onClick={() => { navigator.clipboard.writeText(setupAdmin.email); toast.success("Email copied"); }}
                    className="ml-auto text-[#A0A0A0] hover:text-white"
                    title="Copy email"
                  >
                    <Copy size={13} />
                  </button>
                </div>
                {isAdmin && (
                  <button
                    onClick={removeSetupAdmin}
                    disabled={setupAdminLoading}
                    className="self-start text-sm text-red-400 border border-red-400/30 rounded-lg px-4 py-2 hover:bg-red-500/10 transition disabled:opacity-50"
                  >
                    {setupAdminLoading ? "Removing…" : "Remove Setup Admin"}
                  </button>
                )}
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                <div className="text-xs text-[#A0A0A0]">
                  No setup admin exists. Creating one will allow login as{" "}
                  <span className="font-mono text-white/70">setup+{venue.venue_code.toLowerCase()}@tabon.app</span>.
                </div>
                {isAdmin ? (
                  <button
                    onClick={createSetupAdmin}
                    disabled={setupAdminLoading}
                    className="self-start bg-[#9D00FF] hover:bg-[#8C00E6] disabled:opacity-50 text-white text-sm font-semibold rounded-lg px-4 py-2 transition"
                  >
                    {setupAdminLoading ? "Creating…" : "Create Setup Admin"}
                  </button>
                ) : (
                  <p className="text-xs text-[#A0A0A0] italic">Only Admins can create setup admins.</p>
                )}
              </div>
            )}
          </Card>
          <Card title="Venue Snapshot" className="lg:col-span-2">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Mini label="Total Customers" value={venue.total_customers ?? 0} />
              <Mini label="Total Visits" value={venue.total_visits ?? 0} />
              <Mini label="Active Bottle Tabs" value={venue.active_tabs ?? 0} />
              <Mini label="Total Orders" value={venue.total_orders ?? 0} />
              <Mini label="Health Score" value={venue.health_score != null ? `${venue.health_score}%` : "—"} />
              <Mini label="Health Label" value={venue.health_label || "—"} />
              <Mini label="Last Activity" value={venue.last_activity_at ? formatDate(venue.last_activity_at) : "—"} />
              <Mini label="Onboarded" value={formatDate(venue.onboarded_at)} />
            </div>
          </Card>
        </div>
      )}

      {tab === "Customers" && (
        <Card title={`Customers (${customers.length})`}>
          <Table
            cols={["Name", "Phone", "Segment", "Visits", "Points", "Last Visit", "WA Opt-in", "Joined", "Enrolled via"]}
            rows={customers.map((c) => [
              c.name,
              c.phone,
              c.segment || "—",
              c.total_visits ?? 0,
              c.points_balance ?? 0,
              c.last_visit_at ? formatDate(c.last_visit_at) : "—",
              c.whatsapp_opt_in ? "✅ Yes" : "❌ No",
              c.created_at ? formatDate(c.created_at) : "—",
              (() => {
                const map = {
                  billing_counter: "Billing counter",
                  menu_scan: "Menu QR",
                  delivery: "Delivery QR",
                  menu_bottle_check: "Bottle check",
                  bulk_import: "Bulk import",
                  split_claim: "Points split",
                };
                return map[c.source] || "—";
              })(),
            ])}
            testid="sa-vd-customers-table"
          />
        </Card>
      )}

      {tab === "Bottle Tabs" && (
        <Card title={`Bottle Tabs (${tabs.length})`}>
          <Table
            cols={["Customer", "Brand", "Remaining ML", "Status", "Expiry"]}
            rows={tabs.map((t) => [t.customer_name, t.brand, `${t.remaining_ml} / ${t.bottle_size}`, t.status, formatDate(t.expiry_date)])}
            testid="sa-vd-tabs-table"
          />
        </Card>
      )}

      {tab === "Campaigns" && (
        <div className="flex flex-col gap-4">
          <Card title={`Event Campaigns (${campaigns.length})`}>
            {campaigns.length === 0 ? (
              <p className="text-sm text-[#A0A0A0]">No event campaigns found for this venue.</p>
            ) : (
              <div className="flex flex-col gap-4">
                {campaigns.map((c) => {
                  const s = c.stats || {};
                  const sent = c.sent || 0;
                  const total = c.total || 0;
                  // Read implies delivered — Reached = anyone who got it (delivered + read)
                  const reached = (s.delivered || 0) + (s.read || 0);
                  const reachedPct = sent > 0 ? Math.round((reached / sent) * 100) : 0;
                  const readPct = sent > 0 ? Math.round(((s.read || 0) / sent) * 100) : 0;
                  return (
                    <div key={c.id} className="bg-[#0F0F0F] border border-white/8 rounded-xl p-4">
                      {/* Campaign header */}
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase ${
                              c.status === "completed" ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30" :
                              c.status === "sending" ? "bg-blue-500/15 text-blue-300 border border-blue-500/30" :
                              "bg-amber-500/15 text-amber-300 border border-amber-500/30"
                            }`}>
                              {c.status}
                            </span>
                            <span className="text-[10px] text-[#555] uppercase tracking-wider">{c.target_segment || "All"} segment</span>
                          </div>
                          <p className="text-xs text-[#A0A0A0] line-clamp-2 mt-1">{c.message_text || "—"}</p>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="text-xs text-[#555]">{formatDate(c.created_at)}</p>
                          {c.completed_at && (
                            <p className="text-[10px] text-[#444]">Done {formatDate(c.completed_at)}</p>
                          )}
                        </div>
                      </div>

                      {/* Stats grid */}
                      <div className="grid grid-cols-3 gap-2 mb-3">
                        {[
                          { label: "Targeted", value: total, color: "text-white" },
                          { label: "Sent", value: sent, color: "text-blue-300" },
                          { label: "Skipped", value: c.skipped || 0, color: "text-[#A0A0A0]" },
                        ].map(({ label, value, color }) => (
                          <div key={label} className="bg-[#1A1A1A] rounded-lg p-2.5 text-center">
                            <p className={`text-lg font-bold ${color}`}>{value}</p>
                            <p className="text-[10px] text-[#555] uppercase tracking-wider mt-0.5">{label}</p>
                          </div>
                        ))}
                      </div>

                      <div className="grid grid-cols-3 gap-2">
                        {[
                          { label: "Reached", value: reached, pct: reachedPct, color: "text-emerald-300" },
                          { label: "Read", value: s.read || 0, pct: readPct, color: "text-[#9D00FF]" },
                          { label: "Deferred", value: s.deferred || 0, color: "text-blue-300" },
                          { label: "Expired", value: s.expired || 0, color: "text-orange-400" },
                          { label: "Failed", value: s.failed || 0, color: "text-red-400" },
                          { label: "Opted Out", value: s.opted_out || 0, color: "text-amber-400" },
                        ].map(({ label, value, pct, color }) => (
                          <div key={label} className="bg-[#1A1A1A] rounded-lg p-2.5 text-center">
                            <p className={`text-base font-bold ${color}`}>{value}</p>
                            {pct !== undefined && (
                              <p className="text-[10px] text-[#555]">{pct}%</p>
                            )}
                            <p className="text-[10px] text-[#555] uppercase tracking-wider mt-0.5">{label}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>

          {promotions.length > 0 && (
            <Card title={`Win-back / Segmented Promotions (${promotions.length})`}>
              <Table
                cols={["Title", "Type", "Segment", "Created"]}
                rows={promotions.map((c) => [c.title, c.type, c.target_segment, formatDate(c.created_at)])}
                testid="sa-vd-campaigns-table"
              />
            </Card>
          )}
        </div>
      )}

      <TooltipProvider>
      {tab === "WhatsApp Log" && (
        <Card title={`WhatsApp Log (showing ${waLog.length} of ${waLogTotal})`}>
          <div className="flex flex-wrap gap-3 mb-4 items-center">
            <div className="flex items-center gap-2">
              <label className="text-xs text-[#A0A0A0] uppercase tracking-wider font-semibold">From</label>
              <input
                type="date"
                value={waFrom}
                onChange={(e) => setWaFrom(e.target.value)}
                className="bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white focus:border-[#9D00FF] focus:outline-none"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs text-[#A0A0A0] uppercase tracking-wider font-semibold">To</label>
              <input
                type="date"
                value={waTo}
                onChange={(e) => setWaTo(e.target.value)}
                className="bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white focus:border-[#9D00FF] focus:outline-none"
              />
            </div>
            {(waFrom || waTo) && (
              <button
                onClick={() => { setWaFrom(""); setWaTo(""); }}
                className="text-xs text-[#A0A0A0] hover:text-white underline"
              >
                Clear
              </button>
            )}
          </div>
          <Table
            cols={["Timestamp", "Event", "Customer", "Phone", "Sent Via", "Status", "Error", "Action"]}
            rows={waLog.map((w) => [
              formatDateTime(w.sent_at),
              w.event,
              w.customer_name || "—",
              w.customer_phone || w.phone || "—",
              w.sent_via || "—",
              w.status,
              w.error ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="cursor-help underline decoration-dotted">
                      {w.error.slice(0, 40) + (w.error.length > 40 ? "…" : "")}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs break-words">
                    {w.error}
                  </TooltipContent>
                </Tooltip>
              ) : "—",
              (w.status === "Failed" || w.status === "Timeout") &&
              !NON_RETRYABLE_TEMPLATES.has(w.template_name) &&
              (w.retry_count ?? 0) === 0 ? (
                <button
                  onClick={() => handleRetry(w.id)}
                  disabled={retrying[w.id]}
                  className="text-xs font-semibold text-[#D78CFF] hover:text-white disabled:opacity-50"
                >
                  {retrying[w.id] ? "Retrying…" : "Retry"}
                </button>
              ) : w.retry_count > 0 ? (
                <span className="text-[10px] text-[#A0A0A0]">Retried</span>
              ) : (
                <span className="text-[#A0A0A0]">—</span>
              ),
            ])}
            testid="sa-vd-wa-table"
          />
          {waLog.length < waLogTotal && (
            <div className="mt-4 flex justify-center">
              <button
                type="button"
                onClick={() => setWaLogSkip(waLog.length)}
                className="px-5 py-2 rounded-lg border border-white/10 text-[#A0A0A0] hover:text-white hover:border-white/30 text-xs transition"
              >
                Load more ({waLogTotal - waLog.length} remaining)
              </button>
            </div>
          )}
        </Card>
      )}
      </TooltipProvider>

      {tab === "Activity / Notes" && (
        <Card title="Activity Log">
          <div className="mb-4 flex gap-2">
            <input
              data-testid="sa-vd-note-input"
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              placeholder="Add a note…"
              className="flex-1 bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[#9D00FF]"
            />
            <button
              onClick={addNote}
              data-testid="sa-vd-note-save"
              className="bg-[#9D00FF] hover:bg-[#8C00E6] rounded-lg px-4 py-2 text-sm font-semibold text-white"
            >
              Add
            </button>
          </div>
          <div className="flex flex-col divide-y divide-white/5">
            {notes.length === 0 && <div className="py-4 text-[#A0A0A0] text-sm">No notes yet.</div>}
            {notes.map((n) => (
              <div key={n.id} className="py-3 flex items-start justify-between gap-3">
                <div>
                  <div className="text-sm text-white">{n.text}</div>
                  <div className="text-[11px] text-[#A0A0A0] mt-1">
                    {n.type === "note" ? "Note" : "System"} · {n.created_by_name || "System"} · {formatDate(n.created_at)}
                  </div>
                </div>
                {n.type !== "note" && <span className="text-[10px] uppercase tracking-wider text-[#A0A0A0] border border-white/10 rounded-full px-2 py-0.5">system</span>}
              </div>
            ))}
          </div>
        </Card>
      )}

      {tab === "Usage" && (
        <>
          <Card title="WhatsApp Messages — Monthly">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-[#A0A0A0] border-b border-white/10">
                    <th className="text-left py-2 pr-4 font-medium">Month</th>
                    <th className="text-right py-2 pr-4 font-medium">Messages Sent</th>
                    <th className="text-right py-2 pr-4 font-medium">Free Tier</th>
                    <th className="text-right py-2 pr-4 font-medium">Overage</th>
                    <th className="text-right py-2 font-medium">Overage Charge</th>
                  </tr>
                </thead>
                <tbody>
                  {(usage.months || []).map((m, i) => (
                    <tr key={m.month} className="border-b border-white/5">
                      <td className="py-2.5 pr-4 font-medium text-white">
                        {m.month}
                        {i === 0 && (
                          <span className="ml-2 text-[10px] bg-[#9D00FF]/20 text-[#D78CFF] border border-[#9D00FF]/30 px-1.5 py-0.5 rounded font-bold uppercase tracking-wider">
                            Current
                          </span>
                        )}
                      </td>
                      <td className="py-2.5 pr-4 text-right font-bold text-white">
                        {(m.sent || 0).toLocaleString()}
                      </td>
                      <td className="py-2.5 pr-4 text-right text-[#A0A0A0]">
                        {(m.free_tier || 0).toLocaleString()}
                      </td>
                      <td className={`py-2.5 pr-4 text-right font-semibold ${m.overage > 0 ? "text-[#F59E0B]" : "text-emerald-400"}`}>
                        {m.overage > 0 ? `+${m.overage.toLocaleString()}` : "—"}
                      </td>
                      <td className={`py-2.5 text-right font-bold ${m.overage_charge > 0 ? "text-[#F59E0B]" : "text-[#A0A0A0]"}`}>
                        {m.overage_charge > 0 ? `₹${m.overage_charge.toLocaleString()}` : "₹0"}
                      </td>
                    </tr>
                  ))}
                  {(usage.months || []).length === 0 && (
                    <tr>
                      <td colSpan={5} className="py-4 text-center text-[#A0A0A0] text-sm">
                        No data yet
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <p className="text-[11px] text-[#A0A0A0] mt-3">
              Free tier: {(usage.free_tier || 500).toLocaleString()} messages/month · ₹1 per message above free tier · Counts Sent, Delivered, and Read only
            </p>
          </Card>

          <Card title="Last 12 Weeks — Usage">
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={usage.weeks || []}>
              <CartesianGrid stroke="#ffffff10" />
              <XAxis dataKey="week" stroke="#A0A0A0" fontSize={11} />
              <YAxis stroke="#A0A0A0" fontSize={11} allowDecimals={false} />
              <RechartsTooltip contentStyle={{ background: "#1A1A1A", border: "1px solid #9D00FF33" }} />
              <Bar dataKey="tabs" fill="#9D00FF" />
              <Bar dataKey="visits" fill="#D78CFF" />
              <Bar dataKey="whatsapp" fill="#F59E0B" />
              <Bar dataKey="staff_actions" fill="#10b981" />
            </BarChart>
          </ResponsiveContainer>
          <div className="flex flex-wrap gap-3 text-[11px] text-[#A0A0A0] mt-3">
            <span><span className="inline-block w-2 h-2 bg-[#9D00FF] rounded-sm mr-1.5"></span>Tabs</span>
            <span><span className="inline-block w-2 h-2 bg-[#D78CFF] rounded-sm mr-1.5"></span>Visits</span>
            <span><span className="inline-block w-2 h-2 bg-[#F59E0B] rounded-sm mr-1.5"></span>WhatsApp</span>
            <span><span className="inline-block w-2 h-2 bg-emerald-500 rounded-sm mr-1.5"></span>Staff Actions</span>
          </div>
        </Card>
        </>
      )}

      {tab === "Features" && (
        <>
        <Card title="Venue Feature Flags">
          <p className="text-xs text-[#A0A0A0] mb-4">Changes save automatically. Disabled features are hidden from the venue's navigation.</p>
          <div className="flex flex-col divide-y divide-white/5">
            {FEATURE_DEFS.map(({ key, label, desc }) => {
              const DEFAULT_ON = key !== "call_waiter" && key !== "ordering" && key !== "ordering_qr";
              const enabled = venue.features
                ? Boolean(venue.features[key] ?? DEFAULT_ON)
                : DEFAULT_ON;
              return (
                <div key={key} className="flex items-start justify-between py-3.5 gap-4">
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-white">{label}</div>
                    <div className="text-xs text-[#A0A0A0] mt-0.5">{desc}</div>
                    {key === "ordering_qr" && !venue.features?.ordering && (
                      <p className="text-xs text-amber-400 mt-1">
                        ⚠ Enable "QR Ordering & POS" first
                      </p>
                    )}
                    {key === "ordering_qr" && venue.features?.ordering && !venue.features?.qr_menu && (
                      <p className="text-xs text-amber-400 mt-1">
                        ⚠ Also enable "QR Menu" for customer self-ordering
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => isAdmin && toggleFeature(key, !enabled)}
                    disabled={!isAdmin}
                    data-testid={`feature-toggle-${key}`}
                    title={!isAdmin ? "Admin only" : ""}
                    className={`relative w-12 h-6 rounded-full border-2 transition-colors duration-200 focus:outline-none ${
                      enabled
                        ? "bg-[#9D00FF] border-[#9D00FF]"
                        : "bg-[#2A2A2A] border-white/20"
                    } ${!isAdmin ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                  >
                    <span
                      className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${
                        enabled ? "translate-x-6" : "translate-x-0.5"
                      }`}
                    />
                  </button>
                </div>
              );
            })}
          </div>
          {!isAdmin && <p className="text-xs text-[#A0A0A0] mt-3 italic">Only Admins can change feature flags.</p>}
        </Card>

        <Card title="Operational Settings" className="mt-4">
          <p className="text-xs text-[#A0A0A0] mb-4">Controls how the bill settlement form behaves for this venue.</p>
          <div>
            <div className="text-sm font-semibold text-white mb-1">Bill / Receipt Number</div>
            <div className="text-xs text-[#A0A0A0] mb-3">Whether staff must enter a bill number when settling a bill.</div>
            <div className="flex gap-3 flex-wrap">
              {[
                { value: "off", label: "Off", desc: "Field hidden" },
                { value: "optional", label: "Optional", desc: "Staff can leave blank" },
                { value: "required", label: "Required", desc: "Must be filled" },
              ].map(({ value, label, desc }) => {
                const selected = (venue.bill_number_capture || "optional") === value;
                return (
                  <button
                    key={value}
                    type="button"
                    disabled={!isAdmin}
                    onClick={() => isAdmin && updateVenueSetting("bill_number_capture", value)}
                    className={`flex flex-col items-start px-4 py-2.5 rounded-lg border text-left transition-colors ${
                      selected
                        ? "bg-[#9D00FF]/20 border-[#9D00FF] text-white"
                        : "border-white/10 text-[#A0A0A0] hover:border-white/30"
                    } ${!isAdmin ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                  >
                    <span className="text-sm font-semibold">{label}</span>
                    <span className="text-[10px] mt-0.5">{desc}</span>
                  </button>
                );
              })}
            </div>
          </div>
          <div className="mt-5">
            <div className="text-sm font-semibold text-white mb-1">Points Expiry</div>
            <div className="text-xs text-[#A0A0A0] mb-3">Whether loyalty points expire after a set period.</div>
            <div className="flex flex-col gap-2">
              {/* Never expires */}
              <button
                type="button"
                disabled={!isAdmin}
                onClick={() => isAdmin && updateVenueSetting("points_expiry_months", null)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border text-left transition-colors ${
                  !venue.points_expiry_months
                    ? "bg-[#9D00FF]/20 border-[#9D00FF] text-white"
                    : "border-white/10 text-[#A0A0A0] hover:border-white/30"
                } ${!isAdmin ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
              >
                <span className="text-sm font-semibold">Points never expire</span>
              </button>
              {/* Expires after N months */}
              <div
                onClick={() => isAdmin && !venue.points_expiry_months && updateVenueSetting("points_expiry_months", expiryMonths)}
                className={`flex items-center gap-3 px-4 py-2.5 rounded-lg border transition-colors ${
                  venue.points_expiry_months
                    ? "bg-[#9D00FF]/20 border-[#9D00FF] text-white"
                    : "border-white/10 text-[#A0A0A0] hover:border-white/30"
                } ${!isAdmin ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
              >
                <span className="text-sm font-semibold whitespace-nowrap">Points expire after</span>
                <input
                  type="number"
                  min={1}
                  max={24}
                  disabled={!isAdmin}
                  value={expiryMonths}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => {
                    const v = Math.max(1, Math.min(24, parseInt(e.target.value, 10) || 1));
                    setExpiryMonths(v);
                  }}
                  onBlur={(e) => {
                    if (!isAdmin) return;
                    const v = Math.max(1, Math.min(24, parseInt(e.target.value, 10) || 1));
                    updateVenueSetting("points_expiry_months", v);
                  }}
                  data-testid="points-expiry-months-input"
                  className="w-14 bg-[#0A0A0A] border border-white/20 rounded px-2 py-1 text-sm text-center text-white focus:border-[#9D00FF] focus:outline-none disabled:opacity-50"
                />
                <span className="text-sm">months</span>
              </div>
            </div>
          </div>
          {!isAdmin && <p className="text-xs text-[#A0A0A0] mt-3 italic">Only Admins can change settings.</p>}
        </Card>
        </>
      )}

      {tab === "WA Templates" && (
        <Card title="Automated WhatsApp Templates">
          <p className="text-xs text-[#A0A0A0] mb-4">
            All system templates — shows which are active based on this venue's feature flags.
          </p>
          <div className="flex flex-col divide-y divide-white/5">
            {waTemplates.length === 0 && (
              <div className="py-4 text-[#A0A0A0] text-sm">Loading templates…</div>
            )}
            {waTemplates.map((t) => (
              <div key={t.name} data-testid={`sa-tpl-${t.name}`} className="py-3.5 flex items-start gap-3">
                <span
                  className={`mt-0.5 shrink-0 text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider border ${
                    t.feature_enabled
                      ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30"
                      : "bg-white/5 text-[#A0A0A0] border-white/10"
                  }`}
                >
                  {t.feature_enabled ? "On" : "Off"}
                </span>
                <div>
                  <div className="text-sm font-semibold text-white">{t.label}</div>
                  <div className="text-xs text-[#A0A0A0] mt-0.5">{t.trigger}</div>
                  <div className="text-[10px] text-[#9D00FF] mt-1 font-mono">
                    {t.name} · requires: {t.requires_feature.replace(/_/g, " ")}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {tab === "WA Config" && (
        <Card title="WhatsApp Configuration">
          <p className="text-xs text-[#A0A0A0] mb-5">
            Configure this venue's Meta WhatsApp Cloud API credentials. Leave blank to send via TabOn's shared number.
            Changes take effect immediately on the next message send.
          </p>

          {waConfig && (
            <div className="mb-5 flex items-center gap-2">
              {waConfig.has_venue_number ? (
                <span className="text-[10px] font-bold uppercase tracking-wider bg-emerald-500/15 text-emerald-300 border border-emerald-500/40 rounded-full px-3 py-1">
                  ✅ Using venue's own Meta WhatsApp number
                </span>
              ) : (
                <span className="text-[10px] font-bold uppercase tracking-wider bg-white/10 text-[#A0A0A0] border border-white/20 rounded-full px-3 py-1">
                  Using TabOn's shared Meta number
                </span>
              )}
            </div>
          )}

          <div className="flex flex-col gap-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0] mb-1.5 block">
                Meta Phone Number ID
              </label>
              <input
                value={waConfigForm.meta_phone_number_id}
                onChange={(e) => setWaConfigForm(f => ({ ...f, meta_phone_number_id: e.target.value }))}
                placeholder="Leave blank to use TabOn's shared number"
                className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 min-h-[44px] text-white focus:border-[#9D00FF] text-sm"
              />
              <p className="text-[11px] text-[#A0A0A0] mt-1">Found in Meta Business Manager → WhatsApp → Phone Numbers.</p>
            </div>

            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0] mb-1.5 block">
                Meta Access Token
              </label>
              {waConfig?.has_venue_token && (
                <p className="text-xs text-[#A0A0A0] mb-1.5">
                  Current token: <span className="font-mono">{waConfig.meta_token_masked}</span> — leave blank to keep existing
                </p>
              )}
              <input
                type="password"
                value={waConfigForm.meta_token}
                onChange={(e) => setWaConfigForm(f => ({ ...f, meta_token: e.target.value }))}
                placeholder={waConfig?.has_venue_token ? "Leave blank to keep existing token" : "Paste Meta access token"}
                className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 min-h-[44px] text-white focus:border-[#9D00FF] text-sm"
              />
            </div>

            <div className="flex gap-3 flex-wrap">
              <button
                type="button"
                disabled={waConfigSaving || !isAdmin}
                onClick={async () => {
                  setWaConfigSaving(true);
                  try {
                    const body = { meta_phone_number_id: waConfigForm.meta_phone_number_id };
                    if (waConfigForm.meta_token.trim()) body.meta_token = waConfigForm.meta_token.trim();
                    await api.patch(`/admin/venues/${vid}/settings`, body);
                    const r = await api.get(`/admin/venues/${vid}/whatsapp-config`);
                    setWaConfig(r.data);
                    setWaConfigForm(f => ({ ...f, meta_token: "" }));
                    toast.success("WhatsApp configuration saved");
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || "Save failed");
                  } finally {
                    setWaConfigSaving(false);
                  }
                }}
                className="bg-[#9D00FF] hover:bg-[#8C00E6] disabled:opacity-50 text-white font-semibold rounded-lg px-5 py-2.5 min-h-[44px] text-sm transition"
              >
                {waConfigSaving ? "Saving…" : "Save WhatsApp Config"}
              </button>

              {waConfig?.has_venue_number && (
                <button
                  type="button"
                  disabled={waConfigSaving || !isAdmin}
                  onClick={async () => {
                    if (!window.confirm("This will remove the venue-specific WhatsApp credentials. Messages will fall back to TabOn's shared number. Continue?")) return;
                    setWaConfigSaving(true);
                    try {
                      await api.patch(`/admin/venues/${vid}/settings`, {
                        meta_phone_number_id: "",
                        meta_token: "",
                      });
                      const r = await api.get(`/admin/venues/${vid}/whatsapp-config`);
                      setWaConfig(r.data);
                      setWaConfigForm({ meta_phone_number_id: "", meta_token: "" });
                      toast.success("Venue WhatsApp credentials cleared — now using TabOn's shared number");
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || "Clear failed");
                    } finally {
                      setWaConfigSaving(false);
                    }
                  }}
                  className="border border-[#EF4444]/40 text-[#EF4444] hover:bg-[#EF4444]/10 disabled:opacity-50 font-semibold rounded-lg px-5 py-2.5 min-h-[44px] text-sm transition"
                >
                  Clear & Use Shared Number
                </button>
              )}
            </div>
            {!isAdmin && <p className="text-xs text-[#A0A0A0] italic">Only Admins can change WhatsApp configuration.</p>}
          </div>
        </Card>
      )}

      {tab === "Payments" && (
        <Card title="Online Payments (provisioning)">
          <p className="text-xs text-[#A0A0A0] mb-4">
            SA-provisioned — requires a KYC-completed merchant account. A venue toggling these
            itself would get a broken checkout, so they live here. The venue only chooses cash vs
            online for takeaway in its own Settings.
          </p>

          {/* Master switch */}
          <div className="flex items-start justify-between py-3.5 gap-4 border-b border-white/5">
            <div className="flex-1">
              <div className="text-sm font-semibold text-white">Payments enabled</div>
              <div className="text-xs text-[#A0A0A0] mt-0.5">Off = no online checkout for this venue, whatever else is set.</div>
            </div>
            <button
              type="button"
              disabled={!isAdmin}
              data-testid="sa-payments-enabled-toggle"
              onClick={() => isAdmin && updateVenueSetting("payments_enabled", !venue.payments_enabled)}
              className={`relative w-12 h-6 rounded-full border-2 transition-colors duration-200 focus:outline-none ${
                venue.payments_enabled ? "bg-[#9D00FF] border-[#9D00FF]" : "bg-[#2A2A2A] border-white/20"
              } ${!isAdmin ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
            >
              <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${
                venue.payments_enabled ? "translate-x-6" : "translate-x-0.5"
              }`} />
            </button>
          </div>

          {/* Provider */}
          <div className="py-3.5 border-b border-white/5">
            <div className="text-sm font-semibold text-white mb-1">Provider</div>
            <div className="text-xs text-[#A0A0A0] mb-3">None disables online payment regardless of the switch above.</div>
            <div className="flex gap-3 flex-wrap">
              {[{ value: "", label: "None" }, { value: "cashfree", label: "Cashfree" }].map(({ value, label }) => {
                const selected = (venue.payment_provider || "") === value;
                return (
                  <button
                    key={value || "none"}
                    type="button"
                    disabled={!isAdmin}
                    data-testid={`sa-payment-provider-${value || "none"}`}
                    onClick={() => isAdmin && updateVenueSetting("payment_provider", value || null)}
                    className={`px-4 py-2.5 rounded-lg border text-sm transition-colors ${
                      selected ? "bg-[#9D00FF]/20 border-[#9D00FF] text-white" : "border-white/10 text-[#A0A0A0] hover:border-white/30"
                    } ${!isAdmin ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Merchant ID */}
          <div className="py-3.5">
            <div className="text-sm font-semibold text-white mb-1">Merchant ID</div>
            <div className="text-xs text-[#A0A0A0] mb-2">The provider's merchant id. For Cashfree this should equal the venue id.</div>
            <input
              key={venue.payment_merchant_id || "empty"}
              type="text"
              data-testid="sa-payment-merchant-id"
              defaultValue={venue.payment_merchant_id || ""}
              disabled={!isAdmin}
              placeholder={venue.id}
              onBlur={(e) => {
                const val = e.target.value.trim();
                if (val !== (venue.payment_merchant_id || "")) updateVenueSetting("payment_merchant_id", val || null);
              }}
              className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-2.5 text-white text-sm focus:border-[#9D00FF] disabled:opacity-50"
            />
            {isAdmin && venue.payment_merchant_id !== venue.id && (
              <button
                type="button"
                onClick={() => updateVenueSetting("payment_merchant_id", venue.id)}
                className="mt-2 text-xs text-[#9D00FF] hover:underline"
              >
                Use venue id ({venue.id})
              </button>
            )}
          </div>
          {!isAdmin && <p className="text-xs text-[#A0A0A0] mt-3 italic">Only Admins can change payment provisioning.</p>}
        </Card>
      )}

      {tab === "POS Integration" && (
        <Card title="POS Integration">
          <p className="text-xs text-[#A0A0A0] mb-4">
            Link this venue to an external POS. TabOn issues a token the POS uses to identify the venue on every call.
            The token is shown only once at creation.
          </p>

          {posNewToken && (
            <div className="mb-5 bg-[#9D00FF]/10 border border-[#9D00FF]/40 rounded-xl p-4">
              <div className="text-xs font-bold uppercase tracking-wider text-[#D78CFF] mb-1">New token — shown only once</div>
              <p className="text-[11px] text-amber-300 mb-2">{posNewToken.message}</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 min-w-0 bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-xs text-white font-mono break-all">{posNewToken.token}</code>
                <button type="button" onClick={() => { navigator.clipboard.writeText(posNewToken.token); toast.success("Token copied"); }}
                  className="shrink-0 bg-white/10 hover:bg-white/20 rounded-lg px-3 py-2 text-xs font-semibold flex items-center gap-1"><Copy size={13} /> Copy</button>
              </div>
              <button type="button" onClick={() => setPosNewToken(null)} className="mt-2 text-[11px] text-[#A0A0A0] hover:text-white">I've saved it — dismiss</button>
            </div>
          )}

          {posIntegrations === null ? (
            <p className="text-sm text-[#A0A0A0]">Loading…</p>
          ) : posIntegrations.length === 0 ? (
            <p className="text-sm text-[#A0A0A0] mb-5">No POS integration linked yet.</p>
          ) : (
            <div className="flex flex-col gap-3 mb-5">
              {posIntegrations.length > 1 && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2 text-xs text-amber-300">
                  This venue is linked to more than one provider — unusual; verify it's intended.
                </div>
              )}
              {posIntegrations.map((p) => (
                <div key={p.id} className="bg-[#0F0F0F] border border-white/10 rounded-xl p-4">
                  <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold capitalize text-white">{p.provider}</span>
                      <span className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 border ${p.status === "active" ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" : "bg-white/5 text-[#A0A0A0] border-white/20"}`}>{p.status}</span>
                    </div>
                    <span className="font-mono text-xs text-[#A0A0A0]">{p.token_masked}</span>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-[11px] text-[#A0A0A0] mb-3">
                    <div>Branch code: <span className="text-white">{p.external_branch_code || "—"}</span></div>
                    <div>Linked: <span className="text-white">{p.linked_at ? formatDateTime(p.linked_at) : "—"}</span></div>
                    <div>Last seen: <span className="text-white">{p.last_seen_at ? formatDateTime(p.last_seen_at) : "never"}</span></div>
                    <div>Rotated: <span className="text-white">{p.token_rotated_at ? formatDateTime(p.token_rotated_at) : "—"}</span></div>
                    {p.notes && <div className="sm:col-span-2">Notes: <span className="text-white">{p.notes}</span></div>}
                  </div>
                  {isAdmin && (
                    <div className="flex gap-2 flex-wrap">
                      <button type="button" disabled={posBusy} onClick={() => posRotate(p.id)}
                        className="bg-white/5 hover:bg-white/10 border border-white/10 disabled:opacity-40 rounded-lg px-3 py-1.5 text-xs font-semibold">Rotate token</button>
                      <button type="button" disabled={posBusy} onClick={() => posSetStatus(p.id, p.status === "active" ? "disabled" : "active")}
                        className="bg-white/5 hover:bg-white/10 border border-white/10 disabled:opacity-40 rounded-lg px-3 py-1.5 text-xs font-semibold">{p.status === "active" ? "Disable" : "Enable"}</button>
                      <button type="button" disabled={posBusy} onClick={() => posDelete(p.id)}
                        className="border border-red-500/30 text-red-400 hover:bg-red-500/10 disabled:opacity-40 rounded-lg px-3 py-1.5 text-xs font-semibold">Remove</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {isAdmin && (
            <div className="border-t border-white/5 pt-4">
              <div className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0] mb-3">Link a POS</div>
              <div className="grid sm:grid-cols-2 gap-3 mb-3">
                <div>
                  <label className="text-[11px] text-[#A0A0A0] block mb-1">Provider</label>
                  <select value={posForm.provider} onChange={(e) => setPosForm(f => ({ ...f, provider: e.target.value }))}
                    className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:border-[#9D00FF]">
                    {POS_PROVIDERS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[11px] text-[#A0A0A0] block mb-1">Branch code (optional)</label>
                  <input value={posForm.external_branch_code} onChange={(e) => setPosForm(f => ({ ...f, external_branch_code: e.target.value }))}
                    placeholder="e.g. Petpooja eateryBranchCode"
                    className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:border-[#9D00FF]" />
                </div>
              </div>
              <div className="mb-3">
                <label className="text-[11px] text-[#A0A0A0] block mb-1">Notes (optional)</label>
                <input value={posForm.notes} onChange={(e) => setPosForm(f => ({ ...f, notes: e.target.value }))}
                  className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:border-[#9D00FF]" />
              </div>
              <button type="button" disabled={posBusy} onClick={posCreate}
                className="bg-[#9D00FF] hover:bg-[#8C00E6] disabled:opacity-50 text-white text-sm font-bold rounded-lg px-5 py-2.5">
                {posBusy ? "Linking…" : "Generate token & link"}
              </button>
            </div>
          )}

          <div className="border-t border-white/5 pt-4 mt-4">
            <button type="button" onClick={() => setPosLogOpen(o => !o)} className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0] hover:text-white">
              {posLogOpen ? "▾" : "▸"} Recent inbound calls ({posLog.length})
            </button>
            {posLogOpen && (
              posLog.length === 0 ? (
                <p className="text-xs text-[#A0A0A0] mt-2">No inbound POS calls logged yet.</p>
              ) : (
                <div className="mt-2 overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead><tr className="text-[10px] uppercase tracking-wider text-[#555] text-left">
                      <th className="pb-1 pr-3">Time</th><th className="pb-1 pr-3">Endpoint</th><th className="pb-1">Status</th></tr></thead>
                    <tbody>
                      {posLog.map((r) => (
                        <tr key={r.id} className="border-t border-white/5">
                          <td className="py-1.5 pr-3 text-[#A0A0A0] whitespace-nowrap">{r.created_at ? formatDateTime(r.created_at) : "—"}</td>
                          <td className="py-1.5 pr-3 text-white">{r.endpoint}</td>
                          <td className={`py-1.5 font-mono ${r.status_code >= 400 ? "text-red-400" : "text-emerald-300"}`}>{r.status_code}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            )}
          </div>
        </Card>
      )}

      {tab === "Transfer" && (
        <TransferTab vid={vid} isAdmin={isAdmin} />
      )}
    </SALayout>
  );
}

function Kpi({ label, value }) {
  return (
    <div className="bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 min-w-[120px]">
      <div className="text-[10px] uppercase tracking-wider text-[#A0A0A0]">{label}</div>
      <div className="text-sm font-bold text-white">{value}</div>
    </div>
  );
}
function Card({ title, children, className = "" }) {
  return (
    <div className={`bg-[#1A1A1A] border border-white/10 rounded-xl p-5 ${className}`}>
      <div className="text-sm font-semibold mb-3">{title}</div>
      {children}
    </div>
  );
}
function Mini({ label, value }) {
  return (
    <div className="bg-[#0A0A0A] border border-white/10 rounded-lg p-3">
      <div className="text-[10px] uppercase tracking-wider text-[#A0A0A0] mb-1">{label}</div>
      <div className="text-lg font-bold text-white">{value}</div>
    </div>
  );
}
function Table({ cols, rows, testid }) {
  return (
    <div className="overflow-x-auto" data-testid={testid}>
      <table className="min-w-full text-sm">
        <thead className="text-[#A0A0A0] text-xs uppercase tracking-wider">
          <tr>{cols.map((c) => <th key={c} className="px-3 py-2 text-left font-semibold">{c}</th>)}</tr>
        </thead>
        <tbody className="divide-y divide-white/5">
          {rows.length === 0 && <tr><td colSpan={cols.length} className="px-3 py-6 text-center text-[#A0A0A0]">No data.</td></tr>}
          {rows.map((r, i) => (
            <tr key={i} className="hover:bg-white/5">
              {r.map((cell, j) => <td key={j} className="px-3 py-2 text-white/90">{cell}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MetaEditor({ venue, canEdit, onSave }) {
  const [form, setForm] = useState({
    plan: venue.plan, status: venue.status, monthly_amount: venue.monthly_amount,
    payment_status: venue.payment_status, discount: venue.discount || "",
  });
  useEffect(() => {
    setForm({
      plan: venue.plan, status: venue.status, monthly_amount: venue.monthly_amount,
      payment_status: venue.payment_status, discount: venue.discount || "",
    });
  }, [venue]);

  return (
    <div className="space-y-3">
      <Field label="Plan">
        <select
          value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })} disabled={!canEdit}
          data-testid="sa-vd-meta-plan"
          className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
        >
          {PLAN_OPTS.map((p) => <option key={p}>{p}</option>)}
        </select>
      </Field>
      <Field label="Status">
        <select
          value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} disabled={!canEdit}
          data-testid="sa-vd-meta-status"
          className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
        >
          {STATUS_OPTS.map((p) => <option key={p}>{p}</option>)}
        </select>
      </Field>
      <Field label="Monthly Amount (₹)">
        <input
          type="number" min="0" value={form.monthly_amount}
          onChange={(e) => setForm({ ...form, monthly_amount: Number(e.target.value) })} disabled={!canEdit}
          data-testid="sa-vd-meta-mrr"
          className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
        />
      </Field>
      <Field label="Payment Status">
        <select
          value={form.payment_status} onChange={(e) => setForm({ ...form, payment_status: e.target.value })} disabled={!canEdit}
          data-testid="sa-vd-meta-payment"
          className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
        >
          {PAYMENT_OPTS.map((p) => <option key={p}>{p}</option>)}
        </select>
      </Field>
      <Field label="Discount (text)">
        <input
          value={form.discount} onChange={(e) => setForm({ ...form, discount: e.target.value })} disabled={!canEdit}
          placeholder="e.g. 20% off 3 months"
          className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
          data-testid="sa-vd-meta-discount"
        />
      </Field>
      <div className="flex items-center justify-between gap-2 pt-2">
        {canEdit ? (
          <>
            <button
              onClick={() => onSave({ extend_trial_days: 7 }, "Trial extended by 7 days")}
              className="text-xs font-semibold border border-white/15 rounded-lg px-3 py-2 hover:bg-white/5"
              data-testid="sa-vd-meta-extend-trial"
            >
              + Extend Trial 7 days
            </button>
            <button
              onClick={() => onSave(form, "Meta saved")}
              className="flex items-center gap-1.5 bg-[#9D00FF] hover:bg-[#8C00E6] rounded-lg px-4 py-2 text-xs font-semibold text-white"
              data-testid="sa-vd-meta-save"
            >
              <Save size={13} /> Save
            </button>
          </>
        ) : (
          <div className="text-xs text-[#A0A0A0]">Admins only can edit.</div>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-wider text-[#A0A0A0] mb-1">{label}</div>
      {children}
    </label>
  );
}

function ChecklistEditor({ venue, canEdit, onSave }) {
  const initial = { ...(venue.checklist || {}) };
  const [state, setState] = useState(initial);
  useEffect(() => { setState({ ...(venue.checklist || {}) }); }, [venue]);
  const toggle = (k) => {
    if (!canEdit) return;
    const next = { ...state, [k]: !state[k] };
    setState(next);
    onSave(next);
  };
  return (
    <div className="flex flex-col gap-2">
      {CHECKLIST.map((item) => (
        <label
          key={item}
          className={`flex items-center gap-2 text-sm cursor-pointer ${!canEdit && "opacity-80 cursor-default"}`}
          data-testid={`sa-vd-checklist-${item.replace(/\W/g, "-").toLowerCase()}`}
        >
          <input type="checkbox" checked={!!state[item]} onChange={() => toggle(item)} disabled={!canEdit} className="accent-[#9D00FF]" />
          <span className={state[item] ? "text-white line-through text-white/60" : "text-white"}>{item}</span>
        </label>
      ))}
    </div>
  );
}

function TransferTab({ vid, isAdmin }) {
  const [pending, setPending] = useState(null);  // pending transfer info
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ new_email: "", new_name: "", confirmed: false });
  const [submitting, setSubmitting] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const loadTransfer = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/venues/${vid}/transfer`);
      setPending(data.pending ? data : null);
    } catch { setPending(null); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadTransfer(); }, [vid]);

  const activateLink = pending?.token
    ? `${window.location.origin}/activate?token=${pending.token}`
    : null;

  const initiateTransfer = async (e) => {
    e.preventDefault();
    if (!form.confirmed) { toast.error("Please confirm the transfer"); return; }
    if (!form.new_email || !form.new_name) { toast.error("All fields are required"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/admin/venues/${vid}/transfer`, form);
      toast.success("Transfer initiated — copy the activation link and send it to the new owner");
      await loadTransfer();
      setForm({ new_email: "", new_name: "", confirmed: false });
    } catch (e) { toast.error(formatError(e)); }
    finally { setSubmitting(false); }
  };

  const cancelTransfer = async () => {
    setCancelling(true);
    try {
      await api.delete(`/admin/venues/${vid}/transfer`);
      toast.success("Transfer cancelled");
      setPending(null);
    } catch (e) { toast.error(formatError(e)); }
    finally { setCancelling(false); }
  };

  if (loading) return <Card title="Transfer Account Ownership"><div className="text-[#A0A0A0] text-sm">Loading…</div></Card>;

  return (
    <Card title="Transfer Account Ownership">
      <p className="text-xs text-[#A0A0A0] mb-5">
        Transfers full venue admin access to a new owner. The current owner's account will be deactivated once the new owner activates.
        Copy the activation link below and send it to the new owner manually — no email is sent automatically.
      </p>

      {pending?.pending ? (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 flex flex-col gap-3">
          <div className="flex items-center gap-2 text-amber-400 font-semibold text-sm">
            <ArrowRightLeft size={15} /> Transfer Pending
          </div>
          <div className="text-sm text-white">
            Waiting for <span className="font-bold">{pending.new_name}</span> ({pending.new_email}) to verify their email.
          </div>
          <div className="text-xs text-[#A0A0A0]">
            Expires: {formatDate(pending.expires_at)}
          </div>
          {activateLink && (
            <div className="mt-3 flex flex-col gap-1">
              <p className="text-xs text-[#A0A0A0] font-semibold uppercase tracking-wider">Activation Link — send this to the new owner</p>
              <div className="flex items-center gap-2 bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2">
                <span className="text-xs text-white/70 font-mono truncate flex-1">{activateLink}</span>
                <button
                  type="button"
                  onClick={() => { navigator.clipboard.writeText(activateLink); toast.success("Link copied"); }}
                  className="text-xs text-[#9D00FF] font-semibold shrink-0 hover:underline"
                >
                  Copy
                </button>
              </div>
            </div>
          )}
          {isAdmin && (
            <button
              onClick={cancelTransfer}
              disabled={cancelling}
              className="self-start mt-1 text-sm text-red-400 border border-red-400/30 rounded-lg px-4 py-2 hover:bg-red-500/10 transition disabled:opacity-50"
            >
              {cancelling ? "Cancelling…" : "Cancel Transfer"}
            </button>
          )}
        </div>
      ) : (
        <form onSubmit={initiateTransfer} className="flex flex-col gap-4">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#A0A0A0] mb-1 block">New Owner Email</label>
            <input
              type="email" required
              value={form.new_email}
              onChange={(e) => setForm({ ...form, new_email: e.target.value })}
              placeholder="newowner@example.com"
              data-testid="transfer-email"
              className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[#9D00FF]"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#A0A0A0] mb-1 block">New Owner Name</label>
            <input
              type="text" required
              value={form.new_name}
              onChange={(e) => setForm({ ...form, new_name: e.target.value })}
              placeholder="Full name"
              data-testid="transfer-name"
              className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[#9D00FF]"
            />
          </div>
          <label className="flex items-start gap-2 cursor-pointer text-sm">
            <input
              type="checkbox"
              checked={form.confirmed}
              onChange={(e) => setForm({ ...form, confirmed: e.target.checked })}
              className="mt-0.5 accent-[#9D00FF]"
              data-testid="transfer-confirm"
            />
            <span className="text-[#A0A0A0]">
              I confirm this transfers full login access to the new owner and deactivates the current admin account.
            </span>
          </label>
          <button
            type="submit"
            disabled={submitting || !isAdmin}
            data-testid="transfer-submit"
            title={!isAdmin ? "Admin only" : ""}
            className="self-start flex items-center gap-2 bg-[#9D00FF] hover:bg-[#8C00E6] disabled:opacity-50 text-white text-sm font-semibold rounded-lg px-5 py-2.5 transition"
          >
            <ArrowRightLeft size={14} />
            {submitting ? "Initiating…" : "Initiate Transfer"}
          </button>
          {!isAdmin && <p className="text-xs text-[#A0A0A0] italic">Only Admins can initiate transfers.</p>}
        </form>
      )}
    </Card>
  );
}
